import type { AssetReference } from '@opallabs/cassini'
import { SlideType } from '@opallabs/concepts'
import { EditorProps } from '../Editor'

/**
 * @note Used for `Layout.Image`, `Layout.SectionHeader`, `Layout.Text`,
 *   and `Layout.Title`.
 * @see `Presentations::CanvasSlide::ALIGNMENT_OPTIONS`
 */
export enum ImageAlignment {
  BlockStart = 'top',
  InlineEnd = 'right',
  BlockEnd = 'bottom',
  InlineStart = 'left',
}
export const ImageAlignmentGlyph: Record<ImageAlignment, string> = {
  [ImageAlignment.BlockStart]: 'layout-image-top',
  [ImageAlignment.InlineEnd]: 'layout-image-right',
  [ImageAlignment.BlockEnd]: 'layout-image-bottom',
  [ImageAlignment.InlineStart]: 'layout-image-left',
} as const

/**
 * @note Used for `Layout.ImageAndText`.
 * @see `Presentations::CanvasSlide::ALIGNMENT_OPTIONS`
 */
export enum ImageSize {
  Center = 'center',
  Fill = 'fill',
  Fit = 'fit',
}

/**
 * @see `Presentations::CanvasSlide::ALIGNMENT_OPTIONS`
 */
export enum Layout {
  Image = 'image_only',
  ImageAndText = 'text_and_image',
  SectionHeader = 'section_header',
  Text = 'basic_text',
  // referenced in Rails code, but not displayed for an unknown reason
  // Title = 'title',
}
export const LayoutGlyph: Record<Layout, string> = {
  [Layout.Image]: 'layout-image',
  [Layout.ImageAndText]: 'layout-text-image',
  [Layout.SectionHeader]: 'layout-section-header',
  [Layout.Text]: 'layout-text',
} as const
/**
 * @see `Presentations::CanvasSlide::ALIGNMENT_OPTIONS`
 */
export const defaultLayout: Record<
  Layout,
  ImageAlignment | ImageSize | undefined
> = {
  [Layout.Image]: ImageSize.Center,
  [Layout.ImageAndText]: ImageAlignment.InlineEnd,
  [Layout.SectionHeader]: undefined,
  [Layout.Text]: undefined,
} as const

export type Changeset = Partial<Pick<EditorProps, 'autoUpdate'>> &
  Readonly<{
    alignment?: ImageAlignment | ImageSize
    background_asset_reference_id?: number | null
    layout?: Layout
    is_visible_when_presenting?: boolean
    is_content_locked?: boolean
  }>

type BaseProps = Partial<EditorProps> &
  Readonly<{
    backgroundAssetReference?: AssetReference
    layout?: Layout
    onChange(): void
    presentationId: number
    slideId: number
    updateSlide(
      presentationId: number,
      slideId: number,
      changes: Changeset,
      callback: () => void
    ): void
    uploadAsset(
      file: File,
      callback: (assetReference: { data: AssetReference }) => void
    ): void
    visible: boolean
  }>

type CalendarSlide = BaseProps &
  Readonly<{
    alignment?: never
    imageSize?: never
    slideType: Exclude<SlideType, SlideType.Canvas>
    layout?: never
  }>

type ImageSlide = BaseProps &
  Readonly<{
    /**
     * @default ImageSize.Center
     */
    alignment?: ImageSize
    layout: Layout.Image
  }>

type ImageAndTextSlide = BaseProps &
  Readonly<{
    /**
     * @default ImageAlignment.Right
     */
    alignment: ImageAlignment
    imageSize: ImageSize
    layout: Layout.ImageAndText
  }>

type TextOnlySlide = BaseProps &
  Readonly<{
    alignment?: never
    imageSize?: never
    layout: Layout.SectionHeader | Layout.Text
  }>

export type SlideEditorProps =
  | CalendarSlide
  | ImageSlide
  | ImageAndTextSlide
  | TextOnlySlide
